import UpdateAuthClientRequest from '@shared/models/authentication/UpdateAuthClientRequest';
import OrganizationAuthClientModel, {
    OrganizationAuthClientFieldName,
} from '@shared/models/organizations/OrganizationAuthClientModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { getDefaultClient } from './helpers/projectUtils';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';
import useStateRef from './useStateRef';

export function useCurrentOrgAuthClientEditor(initialModel: OrganizationAuthClientModel | null) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { clientId } = useParams();
    const { updateOrgAuthClient, createOrgAuthClient } = OrganizationContainer.useContainer();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const [editClient, setEditClient, editClientRef] = useStateRef<OrganizationAuthClientModel | null>(initialModel);

    useEffect(() => {
        if (initialModel) {
            // Create a copy to avoid mutating the original object
            const modelCopy = {
                ...initialModel,
            };

            setEditClient(modelCopy);
        } else {
            setEditClient(getDefaultClient(organizationId || ''));
        }
    }, [initialModel]);

    const handleFieldChange = <T extends OrganizationAuthClientFieldName>(
        fieldName: T,
        value: OrganizationAuthClientModel[T]
    ) => {
        setEditClient((prevModel) =>
            prevModel
                ? {
                      ...prevModel,
                      [fieldName]: value,
                  }
                : null
        );
    };

    const handleInputChange = <T extends OrganizationAuthClientFieldName>(
        fieldName: T,
        value: OrganizationAuthClientModel[T]
    ) => {
        handleFieldChange(fieldName, value);
    };

    const createUpdateRequest = () => {
        if (editClientRef.current && organizationId) {
            const updatedClient: UpdateAuthClientRequest = editClientRef.current as UpdateAuthClientRequest;
            return updatedClient;
        }
    };

    const handleUpdate = async () => {
        const request = createUpdateRequest();
        try {
            if (!request || !organizationId || !clientId) throw new Error('Missing data');
            const result = await updateOrgAuthClient(organizationId, clientId, request);
            if (result?.resultType === 'Ok' && result?.data) {
                setEditClient(result.data);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<OrganizationAuthClientModel>();
        }
    };

    const handleCreate = async () => {
        const request = createUpdateRequest();
        try {
            if (!request || !organizationId) throw new Error('Missing data');
            const result = await createOrgAuthClient(organizationId, request);
            if (result?.resultType === 'Ok' && result?.data) {
                setEditClient(result.data);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<OrganizationAuthClientModel>();
        }
    };

    const handleAlerts = (result: IResult<OrganizationAuthClientModel>) => {
        if (result?.resultType === 'Ok') {
            showSuccess(t('common:saved'));
        } else {
            showError(t('common:saveError'));
        }
    };

    const handleSave = async () => {
        return editClientRef.current?.id ? await handleUpdate() : await handleCreate();
    };

    const handleSaveWithAlerts = async () => {
        const result = await handleSave();
        handleAlerts(result);
        return result;
    };

    return {
        editClient,
        setEditClient,
        handleFieldChange,
        handleInputChange,
        handleSave,
        handleSaveWithAlerts,
    };
}
